// Right-rail panel: "Other tickets from this contact".
//
// Quick context shortcut: when you're working a ticket, you almost
// always want to know "have we heard from this person before, and how
// did those play out?". This panel shows every ticket from the same
// `sender_email`, ordered newest first, with status + priority badges
// so you can spot recurring problems / VIPs at a glance.
//
// The current ticket is excluded from the list — there's no point
// linking back to the page you're already on. If the contact has no
// other tickets, the panel renders an empty-state row instead of
// disappearing (predictability beats minimalism — users come to expect
// the panel in the same slot every visit).
//
// Data: `useContactRecentTickets(email)` — the newest 20 other tickets plus the
// sender's total, so a very busy sender (a payments robot can have 1,000+) never
// turns this rail into an endless list. The full, filterable, paged list is on
// the contact page.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, ChevronRight, Paperclip } from 'lucide-react';
import {
  useContactRecentTickets,
  useTicketsHaveAttachments,
  type Ticket,
} from '@/lib/queries';
import { Skeleton } from '@/components/ui/skeleton';
import { priorityDot, statusColor, statusLabel } from '@/lib/format';
import { cn } from '@/lib/utils';
import { RailSection } from '@/components/RailSection';

interface Props {
  /** Sender email of the current ticket. The panel hides itself entirely
   *  when this is missing (e.g. malformed inbound). */
  email: string | undefined;
  /** Current ticket's id — excluded from the list so we don't link to
   *  the page the user is already on. */
  currentTicketId: string;
}

// Priority dots, status pills and status labels all come from `lib/format` now.
// This file used to carry its own copies; they had drifted from the canonical
// tables (P2 amber vs orange, P3 yellow vs amber, and `waiting_on_client`
// rendered amber here while the ticket row rendered it purple).

// Compact relative time. Anything older than 30 days falls back to
// "DD MMM" so the user sees a date, not a meaninglessly large
// number of weeks. Mirrors what `formatTicketAge` would do but kept
// inline so this component has no dependency on the full format lib
// shape and can ship as a self-contained panel.
function shortAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

export function ContactTicketsPanel({ email, currentTicketId }: Props) {
  const q = useContactRecentTickets(email);
  const rows = q.data?.rows;

  // Paperclip indicator on rows that have attachments. One bulk query
  // for this contact's tickets — small set, cheap. Shares the react-
  // query cache with the contact page's accordion when both render.
  const ticketIds = useMemo(
    () => (rows ?? []).map((t) => t.id),
    [rows],
  );
  const attachmentsQ = useTicketsHaveAttachments(ticketIds);
  const attachmentIds = attachmentsQ.data;

  // Guard whitespace-only too — an all-spaces sender_email is truthy but
  // would produce a `?email=%20%20` link that hits the "Missing ?email="
  // error page. Hiding the panel matches the null case.
  if (!email || !email.trim()) return null;

  const others = (rows ?? []).filter((t) => t.id !== currentTicketId).slice(0, 20);
  // The total includes the ticket you're on.
  const totalOthers = Math.max(0, (q.data?.count ?? 0) - 1);

  return (
    <RailSection
      title="From this contact"
      icon={Users}
      count={q.data ? totalOthers : undefined}
      collapsedMaxHeight={14}
      /* Footer: link to the full contact page where you also get the
      customer health snapshot, sentiment series, and the "Export
      Journey" button. Always rendered (even on zero other tickets)
      because the page also surfaces the current ticket and contact
      metadata, so the link is still useful. */
      footer={
        <Link
          to={`/contacts/view?email=${encodeURIComponent(email)}`}
          className="group -mx-3 -mb-3 mt-2 flex items-center justify-between rounded-b-md border-t border-border/70 px-3 py-2 text-2xs font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground"
        >
          <span>
            {totalOthers > others.length ? `View all ${totalOthers} on contact page` : 'View all on contact page'}
          </span>
          <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      }
    >
      {q.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : q.isError ? (
        <p className="text-xs text-tint-critical-fg">Couldn't load this contact's tickets.</p>
      ) : others.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No other tickets from this contact yet.
        </p>
      ) : (
        <ul className="-mx-3 divide-y divide-border/60">
          {others.map((t) => (
            <ContactTicketRow
              key={t.id}
              ticket={t}
              hasAttachment={attachmentIds?.has(t.id) ?? false}
            />
          ))}
        </ul>
      )}
    </RailSection>
  );
}

function ContactTicketRow({
  ticket,
  hasAttachment,
}: {
  ticket: Ticket;
  hasAttachment: boolean;
}) {
  return (
    <li>
      <Link
        to={`/tickets/${ticket.id}`}
        className="group flex items-start gap-2 px-3 py-2 hover:bg-accent/50"
      >
        <span
          className={cn(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            priorityDot(ticket.priority),
          )}
          aria-hidden="true"
          title={ticket.priority}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-xs font-medium text-foreground">{ticket.subject}</p>
            {hasAttachment ? (
              <Paperclip
                className="h-3 w-3 shrink-0 text-muted-foreground"
                aria-label="Has attachments"
              />
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-2xs">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-1.5 py-0.5 text-3xs font-medium',
                statusColor(ticket.status),
              )}
            >
              {statusLabel(ticket.status)}
            </span>
            <span className="text-muted-foreground">
              {ticket.resolved_at ? `resolved ${shortAge(ticket.resolved_at)}` : `opened ${shortAge(ticket.created_at)}`}
            </span>
          </div>
        </div>
        <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
      </Link>
    </li>
  );
}
